import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/api';
import type {
  OwnSpace,
  CityOption,
  ContactRequestCreated,
  ContactRequestInput,
  CrefReviewItem,
  HiddenProfile,
  MarketplaceAccount,
  MarketplaceMeta,
  ModerationReport,
  ModerationSummary,
  MyMarketplaceProfile,
  ProfessionalSearch,
  ProfileInput,
  PublicProfile,
  ReceivedContactRequest,
  ReportInput,
  SentContactRequest,
  SuspendedMember,
} from '@/api/types';
import { useAuth } from '@/auth';

const BASE = '/marketplace';

export interface SearchParams {
  q?: string;
  uf?: string;
  city?: string;
  neighborhood?: string;
  mode?: 'online' | 'presencial';
  specialty?: string;
  gender?: 'feminino' | 'masculino';
  maxPriceCents?: number;
  trial?: boolean;
  verified?: boolean;
  includeOnline?: boolean;
  sort?: 'relevance' | 'price';
  page?: number;
  pageSize?: number;
}

export const marketplaceKeys = {
  meta: ['marketplace', 'meta'] as const,
  search: (params: SearchParams) => ['marketplace', 'search', params] as const,
  profile: (slug: string) => ['marketplace', 'profile', slug] as const,
  cities: (q: string, uf?: string) => ['marketplace', 'cities', q, uf ?? ''] as const,
  account: ['marketplace', 'account'] as const,
  sent: ['marketplace', 'requests', 'sent'] as const,
  received: ['marketplace', 'requests', 'received'] as const,
  moderation: ['marketplace', 'moderation'] as const,
};

export function useMarketplaceMeta() {
  return useQuery({
    queryKey: marketplaceKeys.meta,
    queryFn: () => api.get<MarketplaceMeta>(`${BASE}/meta`, { auth: false }),
    staleTime: 5 * 60_000,
  });
}

/** Label of a reference key (specialty, mode...), falling back to the key itself. */
export function useLabels() {
  const meta = useMarketplaceMeta().data;
  const map = (list: { key: string; label: string }[] | undefined) =>
    Object.fromEntries((list ?? []).map((o) => [o.key, o.label])) as Record<string, string>;
  return {
    meta,
    specialty: map(meta?.specialties),
    mode: map(meta?.serviceModes),
    goal: map(meta?.goals),
    priceUnit: map(meta?.priceUnits),
  };
}

export function useProfessionalSearch(params: SearchParams, enabled = true) {
  return useQuery({
    queryKey: marketplaceKeys.search(params),
    queryFn: () =>
      api.get<ProfessionalSearch>(`${BASE}/professionals`, {
        auth: false,
        query: { ...params },
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePublicProfile(slug: string) {
  return useQuery({
    queryKey: marketplaceKeys.profile(slug),
    queryFn: () => api.get<PublicProfile>(`${BASE}/professionals/${slug}`, { auth: false }),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
}

export function useCitySuggestions(q: string, uf?: string) {
  return useQuery({
    queryKey: marketplaceKeys.cities(q, uf),
    queryFn: () =>
      api.get<CityOption[]>(`${BASE}/cities`, { auth: false, query: { q: q || undefined, uf } }),
    staleTime: 60_000,
  });
}

/** The signed-in person's marketplace account (null while signed out). */
export function useMarketplaceAccount() {
  const { status } = useAuth();
  return useQuery({
    queryKey: marketplaceKeys.account,
    queryFn: () => api.get<MarketplaceAccount>(`${BASE}/me`),
    enabled: status === 'authenticated',
    refetchInterval: 60_000,
  });
}

export function useSentRequests() {
  return useQuery({
    queryKey: marketplaceKeys.sent,
    queryFn: () => api.get<SentContactRequest[]>(`${BASE}/me/requests/sent`),
  });
}

export function useReceivedRequests() {
  return useQuery({
    queryKey: marketplaceKeys.received,
    queryFn: () => api.get<ReceivedContactRequest[]>(`${BASE}/me/requests/received`),
    refetchInterval: 60_000,
  });
}

function useInvalidateAccount() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: marketplaceKeys.account }),
      client.invalidateQueries({ queryKey: ['marketplace', 'requests'] }),
    ]);
}

export function useSendRequest(slug: string) {
  const invalidate = useInvalidateAccount();
  return useMutation({
    mutationFn: (body: ContactRequestInput) =>
      api.post<ContactRequestCreated>(`${BASE}/professionals/${slug}/contact`, body),
    onSuccess: invalidate,
  });
}

export type RequestAction = 'accept' | 'decline' | 'block' | 'unblock' | 'cancel';

export function useRequestAction() {
  const invalidate = useInvalidateAccount();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: RequestAction }) =>
      api.post(`${BASE}/requests/${id}/${action}`),
    onSuccess: invalidate,
  });
}

export function useReport() {
  return useMutation({ mutationFn: (body: ReportInput) => api.post(`${BASE}/reports`, body) });
}

export function useSaveProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ProfileInput) => api.put<MyMarketplaceProfile>(`${BASE}/me/profile`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['marketplace'] }),
  });
}

export function usePublishProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (publish: boolean) =>
      api.post<MyMarketplaceProfile>(`${BASE}/me/profile/${publish ? 'publish' : 'unpublish'}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['marketplace'] }),
  });
}

// ── Moderation ───────────────────────────────────────────────────────────────

const MOD = `${BASE}/moderation`;

export function useModerationSummary() {
  return useQuery({
    queryKey: [...marketplaceKeys.moderation, 'summary'],
    queryFn: () => api.get<ModerationSummary>(`${MOD}/summary`),
  });
}

export function useCrefQueue(status: 'pending' | 'verified' | 'rejected') {
  return useQuery({
    queryKey: [...marketplaceKeys.moderation, 'cref', status],
    queryFn: () => api.get<CrefReviewItem[]>(`${MOD}/cref`, { query: { status } }),
  });
}

export function useReportQueue(status: 'open' | 'dismissed' | 'actioned') {
  return useQuery({
    queryKey: [...marketplaceKeys.moderation, 'reports', status],
    queryFn: () => api.get<ModerationReport[]>(`${MOD}/reports`, { query: { status } }),
  });
}

export function useHiddenProfiles() {
  return useQuery({
    queryKey: [...marketplaceKeys.moderation, 'hidden'],
    queryFn: () => api.get<HiddenProfile[]>(`${MOD}/hidden`),
  });
}

export function useSuspendedMembers() {
  return useQuery({
    queryKey: [...marketplaceKeys.moderation, 'suspended'],
    queryFn: () => api.get<SuspendedMember[]>(`${MOD}/suspended`),
  });
}

export function useModerationAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.post(`${MOD}/${path}`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['marketplace'] }),
  });
}

/** pt-BR message for the marketplace's error codes. */
export function marketplaceError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  switch (error.code) {
    case 'open-limit':
      return 'Você já tem 5 pedidos aguardando resposta. Espere uma resposta ou cancele um pedido para enviar outro.';
    case 'daily-limit':
      return 'Você atingiu o limite de pedidos de hoje. Tente de novo amanhã.';
    case 'already-requested':
      return 'Você já pediu contato para este personal.';
    case 'recently-declined':
      return 'Este personal recusou um pedido seu recentemente. Que tal procurar outros profissionais?';
    case 'not-accepting':
      return 'Este personal não está aceitando novos alunos no momento.';
    case 'blocked':
      return 'Este personal não está recebendo pedidos seus.';
    case 'own-profile':
      return 'Este é o seu próprio perfil.';
    case 'already-reported':
      return 'Você já denunciou esta pessoa. A moderação vai analisar.';
    case 'slug-taken':
      return 'Esse endereço já está em uso. Escolha outro.';
    case 'profile-hidden':
      return 'Seu perfil foi ocultado pela moderação. Fale com o suporte.';
    case 'profile-incomplete':
      return 'Preencha os campos obrigatórios antes de publicar.';
    case 'request-closed':
      return 'Este pedido já foi encerrado.';
    default:
      return null;
  }
}

export function useOwnSpaces(enabled = true) {
  return useQuery({
    queryKey: ['marketplace', 'spaces'],
    queryFn: () => api.get<OwnSpace[]>('/marketplace/me/spaces'),
    enabled,
  });
}

export function useCreateOwnSpace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { slug: string; displayName: string; primaryColor: string }) =>
      api.post<OwnSpace[]>('/marketplace/me/spaces', body),
    onSuccess: (data) => client.setQueryData(['marketplace', 'spaces'], data),
  });
}
